import type { ReactNode } from "react";
import { cn } from "../../lib/cn";

type EmptyStatePresentation = "page" | "inline" | "panel" | "list" | "table";

interface EmptyStateProps {
  eyebrow?: string;
  title?: string;
  body?: ReactNode;
  action?: ReactNode;
  /** Reduce padding when the state is inline rather than a dedicated page. */
  inline?: boolean;
  presentation?: EmptyStatePresentation;
  /**
   * Override only the title's heading element (not its visual weight) so the
   * empty state slots into the host page's outline — e.g. an h1 when it is the
   * page's own heading, or an h2 under a header-less panel. When absent, the
   * element follows the presentation.
   */
  titleLevel?: 1 | 2 | 3;
  className?: string;
}

const PRESENTATION_CLASSES: Record<EmptyStatePresentation, string> = {
  page: "mx-auto max-w-[calc(var(--space-16)*7+var(--space-8))] items-center gap-[var(--space-3)] py-[var(--empty-state-py-page)] text-center",
  inline:
    "mx-auto max-w-[calc(var(--space-16)*7+var(--space-8))] items-center gap-[var(--space-3)] py-[var(--empty-state-py-inline)] text-center",
  panel:
    "w-full items-center gap-[var(--space-2)] px-[var(--layout-card-pad-x)] py-[var(--empty-state-py-inline)] text-center",
  list: "w-full items-start gap-[var(--space-2)] px-[var(--space-4)] py-[var(--space-3)] text-left",
  table:
    "w-full items-center gap-[var(--space-2)] px-[var(--space-3)] py-[var(--space-6)] text-center",
};

export function EmptyState({
  eyebrow,
  title,
  body,
  action,
  inline = false,
  presentation: requestedPresentation,
  titleLevel,
  className,
}: EmptyStateProps) {
  const presentation = requestedPresentation ?? (inline ? "inline" : "page");
  const Heading: "h1" | "h2" | "h3" = titleLevel
    ? (`h${titleLevel}` as "h1" | "h2" | "h3")
    : presentation === "page" || presentation === "inline"
      ? "h2"
      : "h3";
  const Root = title ? "section" : "div";

  return (
    <Root
      role={presentation === "page" ? undefined : "status"}
      className={cn("flex flex-col", PRESENTATION_CLASSES[presentation], className)}
    >
      {eyebrow ? (
        <span className="type-eyebrow text-[var(--color-text-faint)]">{eyebrow}</span>
      ) : null}
      {title ? (
        <Heading
          className={cn(
            "leading-tight text-[var(--color-text)]",
            presentation === "page" || presentation === "inline" ? "type-h2" : "type-panel-title",
          )}
          style={{ fontFamily: "var(--font-display)" }}
        >
          {title}
        </Heading>
      ) : null}
      {body ? (
        <p className="type-body leading-relaxed text-[var(--color-text-secondary)]">{body}</p>
      ) : null}
      {action ? <div className="mt-[var(--space-2)]">{action}</div> : null}
    </Root>
  );
}

export function EmptyStateListItem(props: Omit<EmptyStateProps, "inline" | "presentation">) {
  return (
    <li>
      <EmptyState {...props} presentation="list" />
    </li>
  );
}

interface EmptyStateTableRowProps extends Omit<EmptyStateProps, "inline" | "presentation"> {
  colSpan: number;
}

export function EmptyStateTableRow({ colSpan, ...props }: EmptyStateTableRowProps) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <EmptyState {...props} presentation="table" />
      </td>
    </tr>
  );
}
